import { persistCampaignInteractionEvent } from 'api/campaigns'
import store, { getStoreState } from 'config/store'
import { HEADER_STATES } from 'constants/header'
import { triggerPlaybookFiredAPIEvent } from 'modules/api/events'
import { clearActiveCampaign, receiveActiveCampaign, receiveEnrolledInteraction, receiveUnreadCampaigns } from 'modules/campaigns/actions'
import { clearActiveConversation, receiveCampaignConversations, receivePreRenderedMessages } from 'modules/conversations/actions'
import {
  receiveConversation, receiveMessagesForConversation, setActiveConversation
} from 'modules/conversations/actions'
import { getActiveDriftlink, getCampaignConversations } from 'modules/conversations/selectors'
import { hasActiveConversationBeenInteractedWith } from 'modules/conversations/selectors'
import { getEmbedId } from 'modules/embed/selectors'
import { getCampaignIdFromInteractionId } from 'modules/embed/utils'
import { sendPlaybookImpression } from 'modules/impressions'
import { removeSendIdFromStore } from 'modules/impressions/utils'
import { getMessageAttribute, hasMessageAttribute } from 'modules/message/utils'
import { chatHeaderType, isChatOpen } from 'modules/view/selectors'
import { filter,isNil,last,omit, path,pathOr, reject } from 'ramda'
import { DRIFT_SESSION_CAMPAIGNS } from 'shared/constants/session'
import { CampaignReenrollmentType } from 'types/campaigns'
import { EmbedCampaign } from 'types/embed'
import { Message, ViewerRecipientStatus } from 'types/message'
import { error, isNilOrEmpty } from 'utils'
import { localStore } from 'utils/storage'

import { fetchPreRenderedPlaybookMessages } from './renderer'
import { getUnreadCampaignsById, hasActiveCampaign } from './selectors'
import { ActiveCampaign } from './types'
import { getAttributeFromCampaignOrDefault } from './utils'

/**
 * Gets all campaign conversations (i.e. non-prerendered campaigns the visitor was enrolled in) and only returns those that have been created this session
 */
const getSessionCampaignConversations = async () => {
  const state = getStoreState()
  const session = state.session || {}
  const sessionStartedTimestamp = pathOr(null, ['visitInformation', 'currentSessionStartedAt'], session)

  if (!sessionStartedTimestamp) {
    return []
  }

  const campaignConversations = getCampaignConversations(state)

  return filter(conversation => conversation.createdAt >= sessionStartedTimestamp, campaignConversations)
}

export const assignActiveCampaignFromEmbed = (embedCampaign: EmbedCampaign, message?: string) => {
  const displayType = getAttributeFromCampaignOrDefault(embedCampaign, ['displayType'])

  const activeCampaign: ActiveCampaign = {
    id: embedCampaign.id,
    attributes: {
      ...embedCampaign.attributes,
      message,
      campaignId: embedCampaign.id,
      campaignDisplayType: displayType
    },
    inboxId: embedCampaign.inboxId
  }

  store.dispatch(receiveActiveCampaign({ data: activeCampaign }))
}

export const assignActiveCampaignFromMessage = (message: Message) => {
  if (isNilOrEmpty(message)) {
    return
  }

  let campaignId = getMessageAttribute(message, 'campaignId')
  if (!campaignId && hasMessageAttribute(message, 'startInteraction')) {
    const interactionId = getMessageAttribute(message, 'startInteraction')
    campaignId = getCampaignIdFromInteractionId(interactionId)
  }

  const messageWithoutAttributes = omit(['attributes'], message)

  const activeCampaign: ActiveCampaign = {
    id: campaignId,
    attributes: message.attributes || {},
    message: messageWithoutAttributes,
    inboxId: message.inboxId
  }

  store.dispatch(receiveActiveCampaign({ data: activeCampaign }))
}

export const storeAnnouncementCampaignCompleted = async (campaignId: number, playbookId: number) => {
  if (!campaignId) {
    return
  }

  await triggerCampaignInteractionEvent(campaignId)

  const impressionCampaign = getCampaignFromEmbedById(campaignId)
  const impressionCampaignReenrollmentType: CampaignReenrollmentType = getAttributeFromCampaignOrDefault(impressionCampaign, ['campaignReenrollmentType'], null)

  if (impressionCampaignReenrollmentType === CampaignReenrollmentType.ALWAYS) {
    await removeSendIdFromStore(playbookId)
    return
  }
}

export const showUnreadCampaign = (campaignId: number) => {
  const state = getStoreState()
  const { campaigns = {} } = state

  const activeDriftlink = getActiveDriftlink(state)
  const { activeCampaign } = campaigns

  // Don't show an unread campaign if the site visitor is already in an active campaign (i.e. driftlink)
  if (!!activeCampaign.id || activeDriftlink) { return }

  const unreadCampaignsById = getUnreadCampaignsById()
  const unreadCampaign = unreadCampaignsById[campaignId]

  if (!unreadCampaign) {
    return
  }

  const { conversation = {}, messages = [] } = unreadCampaign

  const firstMessage = messages[0]
  const playbookId = getMessageAttribute(firstMessage, 'playbookId')
  const interactionId = getMessageAttribute(firstMessage, 'interactionId')
  const isExperiment = getMessageAttribute(firstMessage, 'isExperiment')
  const playbookVersion = getMessageAttribute(firstMessage, 'playbookVersion')

  store.dispatch(receiveConversation({ conversation: { conversation } }))
  store.dispatch(receiveMessagesForConversation({ conversationId: conversation.id, messages: messages }))
  store.dispatch(setActiveConversation({ conversationId: conversation.id }))

  triggerPlaybookFiredAPIEvent({
    ...firstMessage,
    id: null,
    conversationId: null
  })

  assignActiveCampaignFromMessage(firstMessage)
  sendPlaybookImpression({ playbookId, interactionId, isExperiment, playbookVersion })
}

export const evaluateCampaignConversations = (campaignConversations: Record<string, any> = {}) => {
  // Filter campaign list to only show unread campaigns
  const unreadCampaigns = getUnreadCampaigns(campaignConversations)
  store.dispatch(receiveUnreadCampaigns(unreadCampaigns))
  store.dispatch(receiveCampaignConversations(campaignConversations))
}

export const getUnreadCampaignIds = (campaignConversations: Record<string, any> = {}) => {
  return getUnreadCampaigns(campaignConversations).map(campaign => campaign.conversation.campaignId)
}

export const getUnreadCampaignIdsFromStore = () => {
  const { campaigns } = getStoreState()
  const { unreadCampaigns } = campaigns

  return unreadCampaigns.map(campaign => campaign.conversation.campaignId)
}

export const getUnreadCampaigns = (campaignConversations: Record<string, any> = {}) => {
  const isBotCampaign = (campaignConversation) => pathOr(null, ['conversation', 'type'], campaignConversation) === 'CHAT'
  const isFirstMessageUnread = (campaignConversation) => {
    const nonEmptyMessages = campaignConversation.messages.filter(x => x.body !== "")

    if (isNilOrEmpty(nonEmptyMessages)) {
      return false
    }

    return pathOr(null, [0, 'viewerRecipientStatus'], nonEmptyMessages) !== ViewerRecipientStatus.READ
  }

  return campaignConversations.filter(campaign => isBotCampaign(campaign) && isFirstMessageUnread(campaign))
}

export const getCampaignFromEmbedById = (campaignId: number) => {
  const campaigns = pathOr([], ['embed', 'configuration', 'campaigns'], getStoreState())
  return campaigns.filter(campaign => campaign.id === campaignId)[0]
}

/**
 * Looks up the latest campaign shown within the session from session storage and renders it
 * If the campaignId is in campaignConversations it means the campaign has _not_ been pre-rendered so we have a convo object for it
 * In this case, set the active conversation to show the content
 * If not in campaign conversations attempt to pre-render the playbook
 * @returns (boolean) - whether or not a campaign was rendered to the user
 */
export const showLastCampaignInSession = async (): Promise<boolean> => {
  const { data: recentCampaigns = {} } = await localStore.get(DRIFT_SESSION_CAMPAIGNS)
  const state = getStoreState()
  const embedId = getEmbedId(state)

  if (isNilOrEmpty(recentCampaigns)) {
    return false
  }

  const recentCampaignsForEmbed = recentCampaigns[embedId] || []

  if (!recentCampaignsForEmbed) {
    return
  }

  const mostRecentCampaignId = last<number>(recentCampaignsForEmbed)
  const sessionCampaignConversations = await getSessionCampaignConversations()
  const matchedCampaignConversation = sessionCampaignConversations[mostRecentCampaignId]

  if (matchedCampaignConversation) {
    store.dispatch(setActiveConversation({
      conversationId: path(['id'], matchedCampaignConversation)
    }))
    return true
  }

  const matchingCampaign = getCampaignFromEmbedById(mostRecentCampaignId)
  if(!matchingCampaign) {
    return false
  }

  const interactionId: number = path(['attributes', 'interactionId'], matchingCampaign)
  const playbookId: number = path(['attributes', 'playbookId'], matchingCampaign)

  const botRenderingInstructions = await fetchPreRenderedPlaybookMessages({
    playbookId,
    campaignId: mostRecentCampaignId,
    interactionId,
    inboxId: matchingCampaign.inboxId
  })

  if (!botRenderingInstructions) {
    return false
  }

  store.dispatch(receivePreRenderedMessages({
      campaignId: mostRecentCampaignId,
      messages: botRenderingInstructions?.messages ?? [],
      resumeFlowContext: reject(isNil, botRenderingInstructions.resumeFlowContext)
    }))

  return true
}

/**
 * Keeps track of what driftlinks/startInteraction playbooks have fired for a given load of the widget
 * This is used to ensure that only eligible playbooks can be shown in a given tab
 * @param interactionId - the interactionId of the playbook fired
 */
export const markEnrolledInteraction = (interactionId: number) => {
  store.dispatch(receiveEnrolledInteraction(interactionId))
}

const triggerCampaignInteractionEvent = async (campaignId: number) => {
  const { session } = getStoreState()
  try {
    const endUserId = pathOr(null, ['endUser', 'id'], session)
    return await persistCampaignInteractionEvent({ endUserId: endUserId, campaignId: campaignId, sessionId: session?.campaignRefreshToken })
  } catch (e) {
    error(`Failed to persist campaign interaction: ${e.message}`)
    return null
  }
}

/**
 * Handles the logic for determining if we should not bother to re-target a new campaign to a site visitor.
 * Use this when targeting is re-evaluated within a singular widget load - currently only used for drift.page(...)
 *
 * Currently will skip campaign regargeting if:
 * 1) Is already engaging with content
 * 2) The chat frame is open and there's already an active campaign.
 *
 * @returns {boolean} - Whether campaign retargeting should be skipped
 */
export const shouldSkipCampaignRetargeting = () => {
  const currentState  = getStoreState()

  const hasActiveConvo = hasActiveConversationBeenInteractedWith()
  const hasAnActiveCampaign = hasActiveCampaign(currentState)

  const chatOpen = isChatOpen(currentState)
  const headerType = chatHeaderType(currentState)

  if (hasActiveConvo) {
    return true
  } else if (headerType === HEADER_STATES.WELCOME_MESSAGE) {
    return false
  }

  return (chatOpen && hasAnActiveCampaign)
}

/**
 * A small helper method responsible for removing any current campaign from the end user's view.
 * This method will remove the current campaign and conversation from the redux store, clearing the view
 * Use this when you want to either clear out the view or want to prepare to show a new campaign
 */
export const clearCurrentCampaign = () => {
  store.dispatch(clearActiveCampaign())
  store.dispatch(clearActiveConversation())
}
